import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gift,
  Copy,
  Check,
  CheckCircle,
  GooglePlayLogo,
  ShieldCheck,
  Sparkle,
} from "@phosphor-icons/react";
import { COMPANY, buildPlayStoreUrl } from "../data/site";

const REDIRECT_DELAY_MS = 3500;

export default function ReferralRedirect() {
  const { code: rawCode } = useParams();
  const code = (rawCode || "").trim();
  const targetUrl = useMemo(() => buildPlayStoreUrl(code), [code]);

  const [secondsLeft, setSecondsLeft] = useState(
    Math.ceil(REDIRECT_DELAY_MS / 1000)
  );
  const [copied, setCopied] = useState(false);
  const redirectTimeout = useRef(null);
  const tickInterval = useRef(null);

  useEffect(() => {
    redirectTimeout.current = setTimeout(() => {
      window.location.href = targetUrl;
    }, REDIRECT_DELAY_MS);

    tickInterval.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    return () => {
      clearTimeout(redirectTimeout.current);
      clearInterval(tickInterval.current);
    };
  }, [targetUrl]);

  const openPlayStore = () => {
    clearTimeout(redirectTimeout.current);
    clearInterval(tickInterval.current);
    window.location.href = targetUrl;
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard API unavailable — nothing to fall back to, ignore silently.
    }
  };

  return (
    <div
      data-testid="referral-redirect-page"
      className="relative min-h-screen flex flex-col bg-surface overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-60 [mask-image:radial-gradient(ellipse_at_top,#000_25%,transparent_75%)]" />
      <div className="absolute -top-32 -right-24 h-80 w-80 rounded-full bg-brandBlue/20 blur-3xl" />
      <div className="absolute bottom-0 -left-24 h-72 w-72 rounded-full bg-brandOrange/20 blur-3xl" />

      {/* Minimal brand bar */}
      <div className="relative px-5 sm:px-8 pt-6">
        <Link
          to="/"
          data-testid="referral-home-link"
          className="inline-flex items-center w-fit"
        >
          <img
            src={COMPANY.logo}
            alt={COMPANY.name}
            className="h-12 w-auto object-contain"
          />
        </Link>
      </div>

      <div className="relative flex-1 flex items-center justify-center px-5 sm:px-8 py-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
          className="w-full max-w-md bg-surfaceAlt border border-line rounded-3xl shadow-lg p-7 sm:p-10 text-center"
        >
          {/* App icon with spinning attention ring */}
          <div className="relative mx-auto mb-6 h-20 w-20">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-1.5 rounded-[1.4rem] border-2 border-dashed border-brandBlue/40"
            />
            <img
              src={COMPANY.icon}
              alt="JayesPay app icon"
              className="relative h-20 w-20 rounded-2xl object-contain shadow-md bg-surfaceAlt p-1"
            />
          </div>

          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-line bg-surface text-xs font-semibold tracking-[0.18em] uppercase text-infoText mb-5"
            data-testid="referral-eyebrow"
          >
            <Gift size={14} weight="fill" className="text-brandBlue" />
            Partner Invite
          </div>

          <h1 className="font-heading font-black text-ink900 text-2xl sm:text-3xl tracking-tight leading-[1.1] mb-3">
            You&apos;re invited to{" "}
            <span className="text-gradient-brand">start earning</span> with
            JayesPay
          </h1>

          <p className="text-sm sm:text-base text-ink700 leading-relaxed mb-5">
            A JayesPay partner is already earning commission on recharge,
            bill payments, PAN, banking and more — all from one retailer app.
            Install it now and their referral code applies automatically at
            signup.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-6 text-xs font-medium text-ink700">
            {[
              "Earn on every transaction",
              "5 live services, more weekly",
              "Go live in minutes",
            ].map((t) => (
              <div key={t} className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-brandBlue" />
                <span>{t}</span>
              </div>
            ))}
          </div>

          {code ? (
            <div className="mb-7" data-testid="referral-code-block">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink500 mb-2">
                Your invite code · auto-applied on signup
              </div>
              <div className="flex items-center justify-center gap-2">
                <span className="font-heading font-extrabold text-lg tracking-widest text-ink900 bg-surface border border-lineStrong rounded-xl px-4 py-2.5">
                  {code}
                </span>
                <button
                  type="button"
                  onClick={copyCode}
                  aria-label="Copy referral code"
                  data-testid="referral-copy-code-button"
                  className="h-11 w-11 flex-shrink-0 rounded-xl border border-line bg-surface text-ink900 hover:bg-line/50 transition-colors flex items-center justify-center"
                >
                  {copied ? (
                    <Check size={18} weight="bold" className="text-infoText" />
                  ) : (
                    <Copy size={18} weight="bold" />
                  )}
                </button>
              </div>
              {copied && (
                <div className="mt-1.5 text-xs font-medium text-infoText">
                  Copied to clipboard
                </div>
              )}
            </div>
          ) : null}

          <button
            type="button"
            onClick={openPlayStore}
            data-testid="referral-open-playstore-button"
            className="btn-shimmer brand-glow w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brandNavy text-surfaceAlt text-sm font-bold hover:bg-brandBlue transition-colors"
          >
            <GooglePlayLogo size={18} weight="fill" />
            Download &amp; Start Earning
          </button>

          <div
            className="mt-4 flex items-center justify-center gap-2 text-xs text-ink500"
            data-testid="referral-countdown"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brandBlue/60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brandBlue" />
            </span>
            Opening Google Play in {secondsLeft}s to claim your invite&hellip;
          </div>

          <p className="mt-6 text-xs text-ink500">
            Not redirected?{" "}
            <button
              type="button"
              onClick={openPlayStore}
              className="font-semibold text-infoText hover:underline underline-offset-2"
            >
              Tap here
            </button>{" "}
            or{" "}
            <Link
              to="/"
              data-testid="referral-continue-website-link"
              className="font-semibold text-infoText hover:underline underline-offset-2"
            >
              explore JayesPay first
            </Link>
            .
          </p>
        </motion.div>
      </div>

      {/* Footer strip */}
      <div className="relative px-5 sm:px-8 pb-6">
        <div className="max-w-md mx-auto flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-ink500">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={13} weight="duotone" className="text-infoText" />
            Official JayesPay download link
          </span>
          <span className="hidden sm:inline text-line">•</span>
          <span className="inline-flex items-center gap-1.5">
            <Sparkle size={13} weight="fill" className="text-sky-200" />
            {COMPANY.legalName}
          </span>
        </div>
      </div>
    </div>
  );
}
